/* eslint-disable no-unused-vars */
/* eslint-disable no-param-reassign */
import { useState, useEffect, useCallback } from 'react';

import { useQuery } from 'react-query';

import UserService from '../services/UserService';
import { useMeetingViewStore } from '../store';
import { useUiContext } from '../context/UiContext';

const getCurrentPrevAndNextSections = (sections, currentSectionIndex) => {
  const currentSection = sections[currentSectionIndex];
  const prevSection =
    currentSectionIndex > 0 ? sections[currentSectionIndex - 1] : null;
  const nextSection =
    currentSectionIndex < sections.length - 1
      ? sections[currentSectionIndex + 1]
      : null;

  return { currentSection, prevSection, nextSection };
};

export default function useViewMeetingSectionLogic({
  videoRef,
  section,
  isCurrent,
  setIsPlaying,
  shouldReturnToFulscreen,
  setShouldReturnToFulscreen,
  fullscreenContainerRef,
  setIsSignUpPopupVisible,
  setCurrentSectionIndex,
  currentSectionIndex,
  sectionCount,
  miniVideoRef,
}) {
  const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);
  const [isViewImageSectionModalVisible, setIsViewImageSectionModalVisible] =
    useState(false);
  const [
    shouldDisableQuestionModalAnimationOnEnter,
    setShouldDisableQuestionModalAnimationOnEnter,
  ] = useState(false);
  const [
    shouldDisableQuestionModalAnimationOnExit,
    setShouldDisableQuestionModalAnimationOnExit,
  ] = useState(false);
  const [isUploadDocumentsModalVisible, setIsUploadDocumentsModalVisible] =
    useState(false);

  const { showViewerNotesModal } = useUiContext();

  const { data: user } = useQuery('me', UserService.getMe);

  const setMinSectionIndex = useMeetingViewStore(
    (state) => state.setMinSectionIndex
  );
  const timelineMapping = useMeetingViewStore((state) => state.timelineMapping);

  const isNonPlaybleSection =
    section.type !== 'video' && section.type !== 'audio';
  const switchToNextSectionDuringPlayback = useCallback(
    (sectionIndex) => {
      if (sectionIndex > sectionCount - 1 || sectionIndex < 0) {
        return;
      }
      setMinSectionIndex(sectionIndex);
      setCurrentSectionIndex(sectionIndex);
    },
    [sectionCount, setCurrentSectionIndex, setMinSectionIndex]
  );

  const showQuestionModal = useCallback(() => {
    if (!user) {
      setIsSignUpPopupVisible(true);
    } else {
      const { prevSection, nextSection } = getCurrentPrevAndNextSections(
        timelineMapping,
        currentSectionIndex
      );

      // When there are multiple questions in a row, disable the animation for the question modal, so that when modal with question is closed and a new one is opened, the animation is not triggered (should look like a single modal)
      if (prevSection && prevSection.type === 'question') {
        setShouldDisableQuestionModalAnimationOnEnter(true);
      } else {
        setShouldDisableQuestionModalAnimationOnEnter(false);
        setIsQuestionModalVisible(true);
      }

      setTimeout(() => {
        setIsQuestionModalVisible(true);
      }, 0);

      if (nextSection && nextSection.type === 'question') {
        setTimeout(() => {
          setShouldDisableQuestionModalAnimationOnExit(true);
        }, 250);
      } else {
        setTimeout(() => {
          setShouldDisableQuestionModalAnimationOnExit(false);
        }, 250);
      }
    }

    // Exit fullscreen mode if it's active
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setShouldReturnToFulscreen(true);
    }
  }, [
    currentSectionIndex,
    setIsSignUpPopupVisible,
    setShouldReturnToFulscreen,
    timelineMapping,
    user,
  ]);

  const showViewImageSectionModal = useCallback(() => {
    setIsViewImageSectionModalVisible(true);

    // Exit fullscreen mode if it's active
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setShouldReturnToFulscreen(true);
    }
  }, [setShouldReturnToFulscreen]);

  const showUploadDocumentsModal = useCallback(() => {
    setIsUploadDocumentsModalVisible(true);
    // Exit fullscreen mode if it's active
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setShouldReturnToFulscreen(true);
    }
  }, [setShouldReturnToFulscreen]);

  // If video was in fullscreen mode before opening the modal, return to fullscreen mode
  const returnToFullscreen = useCallback(() => {
    if (shouldReturnToFulscreen) {
      fullscreenContainerRef.current?.requestFullscreen?.();
      setShouldReturnToFulscreen(false);
    }
  }, [
    fullscreenContainerRef,
    setShouldReturnToFulscreen,
    shouldReturnToFulscreen,
  ]);

  // Stop the video when the section is current and non-playable
  useEffect(() => {
    if (isCurrent && isNonPlaybleSection && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);

      // Show the question modal if the section is a question
      if (section.type === 'question') {
        showQuestionModal();
      }

      // Show the notes modal if the section is a note
      if (section.type === 'note') {
        showViewerNotesModal(section.text, () => {
          switchToNextSectionDuringPlayback(currentSectionIndex + 1);

          returnToFullscreen();
        });
      }

      // Show the image modal if the section is an image
      if (section.type === 'image') {
        showViewImageSectionModal();
      }

      // Show the upload documents modal if the section is an upload documents section
      if (section.type === 'document') {
        showUploadDocumentsModal();
      }
    } else if (isCurrent && !isNonPlaybleSection && videoRef.current) {
      setIsPlaying(true);
      videoRef.current.play();

      if (miniVideoRef?.current) {
        miniVideoRef.current.play();
      }
    }
  }, [
    currentSectionIndex,
    isCurrent,
    isNonPlaybleSection,
    miniVideoRef,
    returnToFullscreen,
    section.text,
    section.type,
    setCurrentSectionIndex,
    setIsPlaying,
    setMinSectionIndex,
    showQuestionModal,
    showUploadDocumentsModal,
    showViewImageSectionModal,
    showViewerNotesModal,
    switchToNextSectionDuringPlayback,
    videoRef,
  ]);

  const hideQuestionModal = useCallback(() => {
    const { nextSection } = getCurrentPrevAndNextSections(
      timelineMapping,
      currentSectionIndex
    );

    const shouldHideImmediately =
      !nextSection || nextSection.type !== 'question';

    if (shouldHideImmediately) {
      setIsQuestionModalVisible(false);
    } else {
      setTimeout(() => {
        setIsQuestionModalVisible(false);
      }, 250);
    }

    setTimeout(
      () => {
        switchToNextSectionDuringPlayback(currentSectionIndex + 1);

        if (shouldHideImmediately) {
          returnToFullscreen();
        }
      },
      shouldHideImmediately ? 200 : 0
    );
  }, [
    timelineMapping,
    currentSectionIndex,
    switchToNextSectionDuringPlayback,
    returnToFullscreen,
  ]);

  const hideViewImageSectionModal = useCallback(() => {
    setIsViewImageSectionModalVisible(false);

    setTimeout(() => {
      switchToNextSectionDuringPlayback(currentSectionIndex + 1);

      returnToFullscreen();
    }, 200);
  }, [
    currentSectionIndex,
    returnToFullscreen,
    switchToNextSectionDuringPlayback,
  ]);

  const hideUploadDocumentsModal = useCallback(() => {
    setIsUploadDocumentsModalVisible(false);

    setTimeout(() => {
      switchToNextSectionDuringPlayback(currentSectionIndex + 1);

      returnToFullscreen();
    }, 200);
  }, [
    currentSectionIndex,
    returnToFullscreen,
    switchToNextSectionDuringPlayback,
  ]);

  // Switch to a specific section
  const switchToSection = useCallback(
    (sectionIndex) => {
      setMinSectionIndex(sectionIndex);
      setCurrentSectionIndex(sectionIndex);

      videoRef.current.currentTime = timelineMapping[sectionIndex].startTime;

      if (miniVideoRef?.current) {
        miniVideoRef.current.currentTime =
          timelineMapping[sectionIndex].startTime;
      }
    },
    [
      miniVideoRef,
      setCurrentSectionIndex,
      setMinSectionIndex,
      timelineMapping,
      videoRef,
    ]
  );

  return {
    isQuestionModalVisible,
    hideQuestionModal,
    switchToSection,
    isViewImageSectionModalVisible,
    hideViewImageSectionModal,
    shouldDisableQuestionModalAnimationOnEnter,
    shouldDisableQuestionModalAnimationOnExit,
    isUploadDocumentsModalVisible,
    hideUploadDocumentsModal,
  };
}
